
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ProductCard from '@/components/product/card';
import { Skeleton } from '@/components/ui/skeleton';

interface RelatedProductsProps {
  categoryId: number;
  currentProductId: string;
  limit?: number;
}

export const RelatedProducts = ({ 
  categoryId, 
  currentProductId,
  limit = 4
}: RelatedProductsProps) => {
  const { data: products, isLoading } = useQuery({
    queryKey: ['relatedProducts', categoryId, currentProductId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('listings')
        .select(`
          *,
          categories(name)
        `)
        .eq('category_id', categoryId)
        .eq('is_sold', false)
        .eq('is_deleted', false) // Add filter for deleted products
        .eq('is_active', true) // Also ensure products are active
        .neq('id', parseInt(currentProductId)) // Convert string to number
        .order('created_at', { ascending: false })
        .limit(limit);
        
      if (error) throw error;
      return data || [];
    },
    enabled: !!categoryId && !!currentProductId,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {Array(limit).fill(0).map((_, i) => (
          <Skeleton key={i} className="h-64 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
      {products.map(product => (
        <ProductCard 
          key={product.id}
          id={product.id.toString()} // Convert number to string
          title={product.title}
          price={Number(product.price)}
          location={product.location}
          imageUrl={product.images && product.images[0] ? product.images[0] : ''}
          category={product.categories?.name || ''}
          isPremium={product.is_premium}
          createdAt={product.created_at}
        />
      ))}
    </div>
  );
};

// Add default export to fix the import issue
export default RelatedProducts;
